import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { FileText, Upload, Trash2, Download, AlertTriangle, CheckCircle2, Clock, Plus, X, Eye, Pencil, ShieldCheck, ExternalLink } from 'lucide-react';

const CRITICAL_CATEGORIES = ['Aadhaar', 'PAN', 'Offer Letter', 'Bank Passbook / Cheque', 'Photo'];

function docStatus(doc) {
  const exp = (doc.expires_on || '').trim();
  if (!exp) return { label: doc.verified ? 'Verified' : 'Uploaded', color: doc.verified ? '#2D8B5E' : '#6B7280', bg: doc.verified ? '#ECFDF5' : '#F3F4F6', icon: doc.verified ? CheckCircle2 : FileText };
  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
  if (exp < today) return { label: 'Expired', color: '#D4726A', bg: '#FEF2F2', icon: AlertTriangle };
  if (exp <= in30) return { label: 'Expiring soon', color: '#BA7517', bg: '#FFFBEB', icon: Clock };
  return { label: doc.verified ? 'Verified' : 'Valid', color: '#2D8B5E', bg: '#ECFDF5', icon: doc.verified ? ShieldCheck : CheckCircle2 };
}

export default function DocumentsVault({ personId, canEdit, personName, accessTier }) {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [criticalMissing, setCriticalMissing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ category: 'Aadhaar', title: '', expires_on: '', notes: '', file: null });
  const [newCat, setNewCat] = useState('');
  const [showCatMgr, setShowCatMgr] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const fileRef = useRef(null);

  // Status-only view for viewers who can't see documents
  const statusOnly = accessTier === 'manager' || accessTier === 'basic';

  const refresh = async () => {
    try {
      const [dRes, cRes] = await Promise.all([
        api.get(`/people/${personId}/documents`),
        api.get('/people/doc-categories'),
      ]);
      setDocs(dRes.data.documents || []);
      setCriticalMissing(dRes.data.critical_missing || []);
      setCategories(cRes.data.categories || []);
    } catch (err) {
      // 403 for restricted viewers — fall back to empty vault
      setDocs([]);
      setCriticalMissing([]);
      setCategories([]);
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, [personId]);

  const resetForm = () => {
    setForm({ category: 'Aadhaar', title: '', expires_on: '', notes: '', file: null });
    setShowUpload(false);
    setEditId(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleUpload = async () => {
    // Require a title when category is "Other"
    if (form.category === 'Other' && !form.title.trim()) {
      return alert('Please enter a title for the document (since category is "Other").');
    }
    if (editId) {
      // Metadata-only edit (no new file)
      try {
        await api.put(`/people/${personId}/documents/${editId}`, {
          category: form.category, title: form.title || form.category,
          expires_on: form.expires_on, notes: form.notes,
        });
        resetForm(); refresh();
      } catch (err) { alert('Update failed: ' + (err.response?.data?.detail || err.message)); }
      return;
    }
    if (!form.file) return alert('Please pick a file to upload');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('folder', 'documents');
      const up = await api.post('/cloudinary/upload-doc', fd);
      await api.post(`/people/${personId}/documents`, {
        category: form.category,
        title: form.title || form.category,
        url: up.data.url,
        public_id: up.data.public_id,
        resource_type: up.data.resource_type,
        format: up.data.format,
        bytes: up.data.bytes,
        original_filename: up.data.original_filename,
        expires_on: form.expires_on,
        notes: form.notes,
      });
      resetForm(); refresh();
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Delete "${d.title || d.category}"? This removes the file from storage too.`)) return;
    try {
      await api.delete(`/people/${personId}/documents/${d.id}`);
      refresh();
    } catch (err) { alert('Delete failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleEdit = (d) => {
    setForm({ category: d.category || 'Other', title: d.title || '', expires_on: d.expires_on || '', notes: d.notes || '', file: null });
    setEditId(d.id);
    setShowUpload(true);
  };

  const handleAddCategory = async () => {
    const n = newCat.trim();
    if (!n) return;
    await api.post('/people/doc-categories', { name: n });
    setNewCat('');
    const cRes = await api.get('/people/doc-categories');
    setCategories(cRes.data.categories || []);
  };

  // Group docs by category
  const grouped = {};
  docs.forEach(d => {
    const c = d.category || 'Other';
    if (!grouped[c]) grouped[c] = [];
    grouped[c].push(d);
  });
  const filteredGroups = filterCat ? { [filterCat]: grouped[filterCat] || [] } : grouped;

  if (loading) return <div className="py-8 text-center text-sm" style={{ color: '#6B7280' }}>Loading vault...</div>;

  if (statusOnly) {
    // Show status-only view: document presence, no URLs
    const uploadedCats = Object.keys(grouped);
    return (
      <div className="space-y-3" data-testid="docs-vault-status-only">
        <div className="flex items-center gap-2 px-3 py-2 rounded-md text-xs" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
          <ShieldCheck size={12} /> Status-only view — actual documents visible to HR / CEO only
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {CRITICAL_CATEGORIES.map(c => {
            const has = uploadedCats.includes(c);
            return (
              <div key={c} className="p-2.5 rounded-md border" style={{ borderColor: has ? '#A7F3D0' : '#FCA5A5', backgroundColor: has ? '#ECFDF5' : '#FEF2F2' }} data-testid={`doc-status-${c.replace(/[^a-z0-9]/gi, '-')}`}>
                <p className="text-xs font-semibold" style={{ color: has ? '#065F46' : '#991B1B' }}>{c}</p>
                <p className="text-[10px] mt-0.5" style={{ color: has ? '#2D8B5E' : '#D4726A' }}>{has ? 'On file' : 'Missing'}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="docs-vault">
      {/* Access banner */}
      <div className="flex items-center gap-2 px-3 py-2 rounded-md text-xs" style={{ backgroundColor: '#EFF6FF', color: '#1E3A8A' }}>
        <ShieldCheck size={12} /> Document Vault — files stored on Cloudinary, access logged. {docs.length} document{docs.length === 1 ? '' : 's'} on file.
      </div>

      {/* Critical missing alerts */}
      {criticalMissing.length > 0 && (
        <div className="rounded-md border p-3" style={{ borderColor: '#FCA5A5', backgroundColor: '#FEF2F2' }} data-testid="docs-critical-missing">
          <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: '#991B1B' }}>
            <AlertTriangle size={12} /> Missing critical documents for {personName}
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {criticalMissing.map(c => (
              <span key={c} className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>{c}</span>
            ))}
          </div>
        </div>
      )}

      {/* Toolbar */}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => { if (editId) resetForm(); setShowUpload(!showUpload); }} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="upload-doc-btn">
            <Plus size={13} /> Upload Document
          </button>
          <button onClick={() => setShowCatMgr(!showCatMgr)} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="doc-cat-mgr-btn">
            <Pencil size={11} /> Manage categories
          </button>
          {categories.length > 0 && (
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="ml-auto px-2 py-1 rounded text-xs border" style={{ borderColor: '#E5E1DC' }} data-testid="doc-filter-cat">
              <option value="">All categories ({docs.length})</option>
              {Object.keys(grouped).sort().map(c => <option key={c} value={c}>{c} ({grouped[c].length})</option>)}
            </select>
          )}
        </div>
      )}

      {/* Category manager */}
      {showCatMgr && (
        <div className="bg-white rounded-lg border p-3 space-y-2" style={{ borderColor: '#E5E1DC' }} data-testid="doc-cat-mgr">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Document Categories</p>
            <button onClick={() => setShowCatMgr(false)}><X size={14} style={{ color: '#6B7280' }} /></button>
          </div>
          <div className="flex gap-2">
            <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Add new category (e.g. FSSAI cert)" className="flex-1 px-3 py-1.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="new-doc-cat" />
            <button onClick={handleAddCategory} className="px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="add-doc-cat-btn">Add</button>
          </div>
          <div className="flex flex-wrap gap-1">
            {categories.map(c => (
              <span key={c} className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>{c}</span>
            ))}
          </div>
        </div>
      )}

      {/* Upload form */}
      {showUpload && canEdit && (
        <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#2A7B88', borderWidth: 2 }} data-testid="doc-upload-form">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{editId ? 'Edit Document' : 'Upload New Document'}</p>
            <button onClick={resetForm}><X size={14} style={{ color: '#6B7280' }} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Category *</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="doc-form-category">
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {form.category === 'Other' && (
                <p className="text-[10px] mt-1" style={{ color: '#BA7517' }}>Tip: enter a clear title below so it's easy to find later (e.g. "COVID vaccination cert").</p>
              )}
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>{form.category === 'Other' ? 'Title *' : 'Title (optional)'}</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder={form.category === 'Other' ? 'e.g. COVID vaccination certificate' : 'e.g. Aadhaar — front side'} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: form.category === 'Other' && !form.title ? '#BA7517' : '#E5E1DC' }} data-testid="doc-form-title" />
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Expiry Date (if applicable)</label>
              <input type="date" value={form.expires_on} onChange={e => setForm(f => ({ ...f, expires_on: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="doc-form-expiry" />
            </div>
            {!editId && (
              <div>
                <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>File (PDF / JPG / PNG) *</label>
                <input ref={fileRef} type="file" accept="application/pdf,image/*" onChange={e => setForm(f => ({ ...f, file: e.target.files?.[0] || null }))} className="w-full px-3 py-1.5 rounded-md border text-xs" style={{ borderColor: '#E5E1DC' }} data-testid="doc-form-file" />
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Notes</label>
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. Verified against original on 12 Jan 2026" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="doc-form-notes" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleUpload} disabled={uploading} className="px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#1B2A4A' }} data-testid="doc-submit-btn">
              {uploading ? 'Uploading...' : (editId ? 'Save Changes' : 'Upload')}
            </button>
            <button onClick={resetForm} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Document list by category */}
      {Object.keys(filteredGroups).length === 0 ? (
        <div className="bg-white rounded-lg border p-8 text-center" style={{ borderColor: '#E5E1DC' }}>
          <FileText size={28} style={{ color: '#E5E1DC' }} className="mx-auto mb-2" />
          <p className="text-xs" style={{ color: '#9CA3AF' }}>No documents yet. {canEdit ? 'Upload PAN, Aadhaar, appointment letter, and more.' : ''}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.keys(filteredGroups).sort().map(cat => (
            <div key={cat} className="bg-white rounded-lg border" style={{ borderColor: '#E5E1DC' }} data-testid={`doc-group-${cat.replace(/[^a-z0-9]/gi, '-')}`}>
              <div className="px-3 py-2 border-b" style={{ borderColor: '#F3F4F6', backgroundColor: '#F7F5F2' }}>
                <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: '#1B2A4A' }}>
                  <FileText size={12} /> {cat} <span className="text-[10px]" style={{ color: '#9CA3AF' }}>({filteredGroups[cat].length})</span>
                </p>
              </div>
              <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {filteredGroups[cat].map(d => {
                  const st = docStatus(d);
                  const StIcon = st.icon;
                  return (
                    <div key={d.id} className="px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50" data-testid={`doc-item-${d.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate" style={{ color: '#1B2A4A' }}>{d.title || d.original_filename || cat}</p>
                        <div className="flex items-center gap-2 text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>
                          {d.format && <span className="uppercase">{d.format}</span>}
                          {d.bytes > 0 && <span>{(d.bytes / 1024).toFixed(0)}KB</span>}
                          {d.uploaded_at && <span>· Uploaded {d.uploaded_at.slice(0, 10)}</span>}
                          {d.uploaded_by && <span>· by {d.uploaded_by}</span>}
                          {d.expires_on && <span>· Expires {d.expires_on}</span>}
                        </div>
                        {d.notes && <p className="text-[11px] mt-0.5 italic" style={{ color: '#6B7280' }}>{d.notes}</p>}
                      </div>
                      <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0" style={{ backgroundColor: st.bg, color: st.color }}>
                        <StIcon size={10} /> {st.label}
                      </span>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <a href={d.url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-gray-100" title="View" data-testid={`doc-view-${d.id}`}>
                          <Eye size={13} style={{ color: '#2A7B88' }} />
                        </a>
                        {canEdit && (
                          <>
                            <button onClick={() => handleEdit(d)} className="p-1.5 rounded hover:bg-gray-100" title="Edit metadata" data-testid={`doc-edit-${d.id}`}>
                              <Pencil size={13} style={{ color: '#6B7280' }} />
                            </button>
                            <button onClick={() => handleDelete(d)} className="p-1.5 rounded hover:bg-red-50" title="Delete" data-testid={`doc-delete-${d.id}`}>
                              <Trash2 size={13} style={{ color: '#D4726A' }} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
